export const TYPE_BANK_LINKS_CHECKBOX = 'TYPE_BANK_LINKS_CHECKBOX';
export const TYPE_LANGUAGE_CHANGE = 'TYPE_LANGUAGE_CHANGE';
export const TYPE_SWITCH_ROLE = 'TYPE_SWITCH_ROLE';
export const TYPE_UPDATE_USER_DATA = 'TYPE_UPDATE_USER_DATA';
export const TYPE_ID_CREDIT_STATUS = 'TYPE_ID_CREDIT_STATUS';
export const TYPE_LOGGED_IN = 'TYPE_LOGGED_IN';
export const TYPE_LOGGED_OUT = 'TYPE_LOGGED_OUT';
export const TYPE_SESSION_EXPIRED = 'TYPE_SESSION_EXPIRED';
export const TYPE_PASSWORD_CHANGED = 'TYPE_PASSWORD_CHANGED';

export const banklinksCheckbox = (checked) => ({
    type: TYPE_BANK_LINKS_CHECKBOX,
    banklinksCheckbox: checked
});

export const languageChange = (newLanguage) => ({
    type: TYPE_LANGUAGE_CHANGE,
    newLanguage
});

export const roleChange = (newRole) => ({
    type: TYPE_SWITCH_ROLE,
    newRole
});

export const userDataChange = (newUserData) => ({
    type: TYPE_UPDATE_USER_DATA,
    newUserData,
});

// isIdCredit is fixed at sign-in, but a client's card goes live a quarter of an hour
// after they sign — without this they would keep seeing the application page until they
// logged out and back in.
export const idCreditStatusChange = (hasCredit, accnr) => ({
    type: TYPE_ID_CREDIT_STATUS,
    hasCredit,
    accnr
});

export const loggedIn = (data, nonce) => ({
    type: TYPE_LOGGED_IN,
    data,
    nonce
});

export const loggedOut = () => ({
    type: TYPE_LOGGED_OUT
});

// The user has just set their own password, so the forced-change gate can come down
// without making them log in again.
export const passwordChanged = () => ({
    type: TYPE_PASSWORD_CHANGED
});

// A request reported the session token expired mid-use. Distinct from loggedOut so
// the app can try a silent re-auth instead of treating it as an explicit logout.
export const sessionExpired = () => ({
    type: TYPE_SESSION_EXPIRED
});

export const initialLoginState = {
    authNonce: null,
    privateClientData: null,
    businessClientData: null,
    token: null,
    currentRole: null,
    sessionExpired: false,
    mustChangePassword: false,
};

export default function login(state = initialLoginState, action) {
    switch (action.type) {
        case TYPE_BANK_LINKS_CHECKBOX:
            return { ...state, banklinksCheckbox: action.banklinksCheckbox };

        case TYPE_LANGUAGE_CHANGE:
            // language is for logins without privateClientData, a password login for one
            if (state.privateClientData) {
                return {
                    ...state,
                    language: action.newLanguage,
                    privateClientData: {
                        ...state.privateClientData,
                        language: action.newLanguage,
                    },
                };
            }
            return { ...state, language: action.newLanguage };

        case TYPE_SWITCH_ROLE:
            return { ...state, currentRole: action.newRole };

        case TYPE_UPDATE_USER_DATA:
            return {
                ...state,
                privateClientData: applyUserData(state.privateClientData, action.newUserData),
                currentRole: applyUserData(state.currentRole, action.newUserData),
            };

        case TYPE_ID_CREDIT_STATUS: {
            const isIdCredit = action.hasCredit ? '1' : '0';

            if (!state.currentRole) {
                return state;
            }

            // The account number comes with the flag. The invoices page loads by it, and a
            // card that went live after sign-in left the login without one - flipping the flag
            // alone showed that client an empty credit page until they logged in again.
            const accnr = action.hasCredit && action.accnr ? action.accnr : state.currentRole.accnr;

            if (state.currentRole.isIdCredit === isIdCredit && state.currentRole.accnr === accnr) {
                return state;
            }

            return {
                ...state,
                currentRole: { ...state.currentRole, isIdCredit, accnr },
                privateClientData: state.privateClientData
                    ? { ...state.privateClientData, isIdCredit, accnr }
                    : state.privateClientData,
            };
        }

        case TYPE_LOGGED_IN:
            const defaultBusinessClient = action.data.businessClientData
                ?.find(bClient => bClient.isDefault === '1');
            const fallbackRole = defaultBusinessClient || action.data.privateClientData || action.data.businessClientData[0];
            // When re-authenticating to refresh the session token for the same user,
            // keep the role they already had selected (re-matched against the fresh
            // data) instead of snapping back to the default — otherwise refreshing
            // the token would silently switch the active company/role.
            const preservedRole = matchRole(action.data, state.currentRole);

            return {
                ...state,
                ...action.data,
                currentRole: preservedRole || fallbackRole,
                authNonce: action.nonce || null,
                sessionExpired: false,
            };

        case TYPE_PASSWORD_CHANGED:
            return { ...state, mustChangePassword: false };

        case TYPE_SESSION_EXPIRED:
            return { ...state, sessionExpired: true };

        case TYPE_LOGGED_OUT:
            return { ...state, ...initialLoginState };

        default:
            return state;
    }
}

// Re-match a previously selected role against freshly fetched login data, by its
// stable identity (clientId for a business role, userId for the private role).
// Returns null when the role no longer exists or there was no prior role.
function matchRole(data, role) {
    if (!role) {
        return null;
    }
    if (role.clientId) {
        return Array.isArray(data.businessClientData)
            ? data.businessClientData.find(bClient => bClient.clientId === role.clientId) || null
            : null;
    }
    if (role.userId && data.privateClientData && String(data.privateClientData.userId) === String(role.userId)) {
        return data.privateClientData;
    }
    return null;
}

function applyUserData(prev, next) {
    if (prev.userId !== next.userId) {
        return prev;
    }

    return {
        ...prev,
        fullName: `${next.firstName} ${next.lastName}`,
        email: next.email,
        phone: next.phone,
    };
}
